import { FormEvent, useState } from "react";
import { toast } from "sonner";

import { formatNumber } from "@buildpy/core/lib/format";
import { fieldValueOf, proposedValueError } from "@buildpy/core/modules/process-standard/proposal/rules";
import {
  PROPOSAL_FIELD_LABEL,
  PROPOSAL_FIELDS,
  PROPOSAL_REASON_MAX,
  PROPOSAL_REVIEW_DAYS,
  type ProposalField,
} from "@buildpy/core/modules/process-standard/proposal/types";
import type { ProcessStandardItem } from "@buildpy/core/modules/process-standard/types";
import { Button } from "@buildpy/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";

import { createProposal } from "@/api/actions/process-standard";
import Field from "@/components/field";
import { useRouter } from "@/lib/nav";

import { proposalHref } from "./standards-search";
import { useStandardAction } from "./use-standard-action";

const FIELD_OPTIONS = PROPOSAL_FIELDS.map((field) => ({ value: field, label: PROPOSAL_FIELD_LABEL[field] }));

// 보낼 값 — 빈 칸은 NaN 이라 범위 검사가 「숫자를 적어 주세요」 로 막는다(최종 검사는 액션).
export const proposalInput = (item: Pick<ProcessStandardItem, "code">, versionId: string, field: ProposalField, value: string, reason: string) => ({
  item_code: item.code,
  base_version_id: versionId,
  field,
  proposed_value: value.trim() === "" ? Number.NaN : Number(value),
  reason,
});

interface Props {
  item: ProcessStandardItem;
  versionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// 숫자 제안(#386) — 대표 확인 없이 바로 우리 회사 이름으로 공개 · 7일 동안 다른 시공사가 찬반을 남긴다.
const ProposalDialog = ({ item, versionId, open, onOpenChange }: Props) => {
  const router = useRouter();
  const [field, setField] = useState<ProposalField>("output_per_person_day");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { pending, run } = useStandardAction();
  const current = fieldValueOf(item, field);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input = proposalInput(item, versionId, field, value, reason);
    const invalid = proposedValueError(field, input.proposed_value);
    setError(invalid);
    if (invalid) return;
    run(
      () => createProposal(input),
      (id) => {
        onOpenChange(false);
        setValue("");
        setReason("");
        toast.success("제안을 올렸습니다");
        router.push(proposalHref(id));
      }
    );
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>숫자 제안 · {item.name}</DialogTitle>
          <DialogDescription>
            우리 회사 이름으로 바로 공개되고, {PROPOSAL_REVIEW_DAYS}일 동안 다른 시공사의 찬반을 받습니다.
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-y-3" data-testid="proposal-form" onSubmit={onSubmit}>
          <fieldset className="flex flex-col gap-y-3" disabled={pending}>
            <Field label="바꿀 숫자">
              <SimpleSelect onValueChange={(v) => setField(v as ProposalField)} options={FIELD_OPTIONS} value={field} />
            </Field>
            <p className="text-sm text-muted-foreground tabular-nums">지금 값 · {formatNumber(current)}</p>
            <Field label="제안 값">
              <Input
                aria-invalid={error !== null}
                className="tabular-nums"
                inputMode="decimal"
                name="proposed_value"
                onChange={(e) => setValue(e.target.value)}
                step="any"
                type="number"
                value={value}
              />
            </Field>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Field label="제안 이유 (필수)">
              <Textarea className="min-h-24 text-sm" maxLength={PROPOSAL_REASON_MAX} onChange={(e) => setReason(e.target.value)} value={reason} />
            </Field>
          </fieldset>
          <DialogFooter>
            <Button disabled={pending} onClick={() => onOpenChange(false)} size="sm" type="button" variant="ghost">
              취소
            </Button>
            <Button disabled={pending || reason.trim() === ""} size="sm" type="submit">
              제안 올리기
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalDialog;
